import type { ComponentType } from "react";
import { KgArtifactIcon } from "../../../atoms/KgArtifactIcon";
import { KgBookOpenIcon } from "../../../atoms/KgBookOpenIcon";
import { KgCalendarIcon } from "../../../atoms/KgCalendarIcon";
import { KgFileIcon } from "../../../atoms/KgFileIcon";
import { KgPresentationIcon } from "../../../atoms/KgPresentationIcon";
import { KgDataStoryIcon } from "../../../atoms/KgDataStoryIcon";
import { KgUserIcon } from "../../../atoms/KgUserIcon";
import { KgUsersIcon } from "../../../atoms/KgUsersIcon";
import { KgBusinessRulesIcon } from "../../../atoms/KgBusinessRulesIcon";
import { KgColumnsIcon } from "../../../atoms/KgColumnsIcon";
import { KgDatabaseIcon } from "../../../atoms/KgDatabaseIcon";
import { KgDescriptionIcon } from "../../../atoms/KgDescriptionIcon";
import { KgLinksIcon } from "../../../atoms/KgLinksIcon";
import { KgMetricIcon } from "../../../atoms/KgMetricIcon";
import { KgOntologyOrbitIcon } from "../../../atoms/KgOntologyOrbitIcon";
import { KgPanelTableIcon } from "../../../atoms/KgPanelTableIcon";
import { KgRelationshipIcon } from "../../../atoms/KgRelationshipIcon";
import { KgReportIcon } from "../../../atoms/KgReportIcon";
import { KgSigmaSquareIcon } from "../../../atoms/KgSigmaSquareIcon";
import { KgTribalKnowledgeIcon } from "../../../atoms/KgTribalKnowledgeIcon";
import { KgCatalogLayersIcon } from "../../catalog/kgCatalogSectionIcons";
import type { KgCatalogIconProps } from "../../catalog/kgCatalogSectionIcons";
import type { KgArtifactIconProps } from "../../../atoms/KgArtifactIcon/KgArtifactIcon.types";
import type { KgBookOpenIconProps } from "../../../atoms/KgBookOpenIcon/KgBookOpenIcon.types";
import type { KgCalendarIconProps } from "../../../atoms/KgCalendarIcon/KgCalendarIcon.types";
import type { KgFileIconProps } from "../../../atoms/KgFileIcon/KgFileIcon.types";
import type { KgPresentationIconProps } from "../../../atoms/KgPresentationIcon/KgPresentationIcon.types";
import type { KgDataStoryIconProps } from "../../../atoms/KgDataStoryIcon/KgDataStoryIcon.types";
import type { KgUserIconProps } from "../../../atoms/KgUserIcon/KgUserIcon.types";
import type { KgUsersIconProps } from "../../../atoms/KgUsersIcon/KgUsersIcon.types";
import type { KgBusinessRulesIconProps } from "../../../atoms/KgBusinessRulesIcon/KgBusinessRulesIcon.types";
import type { KgColumnsIconProps } from "../../../atoms/KgColumnsIcon/KgColumnsIcon.types";
import type { KgDatabaseIconProps } from "../../../atoms/KgDatabaseIcon/KgDatabaseIcon.types";
import type { KgDescriptionIconProps } from "../../../atoms/KgDescriptionIcon/KgDescriptionIcon.types";
import type { KgLinksIconProps } from "../../../atoms/KgLinksIcon/KgLinksIcon.types";
import type { KgMetricIconProps } from "../../../atoms/KgMetricIcon/KgMetricIcon.types";
import type { KgOntologyOrbitIconProps } from "../../../atoms/KgOntologyOrbitIcon/KgOntologyOrbitIcon.types";
import type { KgPanelTableIconProps } from "../../../atoms/KgPanelTableIcon/KgPanelTableIcon.types";
import type { KgRelationshipIconProps } from "../../../atoms/KgRelationshipIcon/KgRelationshipIcon.types";
import type { KgReportIconProps } from "../../../atoms/KgReportIcon/KgReportIcon";
import type { KgSigmaSquareIconProps } from "../../../atoms/KgSigmaSquareIcon/KgSigmaSquareIcon.types";
import type { KgTribalKnowledgeIconProps } from "../../../atoms/KgTribalKnowledgeIcon/KgTribalKnowledgeIcon.types";

type KgInspectorIconProps =
  | KgArtifactIconProps
  | KgBookOpenIconProps
  | KgCalendarIconProps
  | KgFileIconProps
  | KgPresentationIconProps
  | KgDataStoryIconProps
  | KgUserIconProps
  | KgUsersIconProps
  | KgBusinessRulesIconProps
  | KgColumnsIconProps
  | KgDatabaseIconProps
  | KgDescriptionIconProps
  | KgLinksIconProps
  | KgMetricIconProps
  | KgOntologyOrbitIconProps
  | KgPanelTableIconProps
  | KgRelationshipIconProps
  | KgReportIconProps
  | KgSigmaSquareIconProps
  | KgTribalKnowledgeIconProps
  | KgCatalogIconProps;

/**
 * KG right-panel icons — import from here only (no ad-hoc feather/lucide in inspector).
 * Add keys when new Figma SVGs are provided.
 */
export const kgInspectorIcons = {
  /** Panel header for table nodes (14px). */
  tablePanel: KgPanelTableIcon,
  /** Panel header for ontology nodes (14px). */
  ontologyPanel: KgOntologyOrbitIcon,
  /** Panel header for metric nodes (14px, Figma `gauge`). */
  metricPanel: KgMetricIcon,
  /** Panel header for datasource reports (14px, Figma `file-text`). */
  reportPanel: KgReportIcon,
  /** Panel header for native (MagicBI) reports (14px, Figma sparkle). */
  reportNativePanel: KgDataStoryIcon,
  /** Panel header for retrieved reports (14px, Figma `book-open`). */
  reportRetrievedPanel: KgBookOpenIcon,
  /** Panel header for data story nodes (14px, Figma sparkle). */
  dataStoryPanel: KgDataStoryIcon,
  /** Panel header for artifact nodes (14px, Figma `presentation`). */
  artifactPanel: KgPresentationIcon,
  /** Panel header for data model nodes (14px, Figma `layers`). */
  dataModelPanel: KgCatalogLayersIcon,
  /** Artifact overview → Format row (16px). */
  format: KgFileIcon,
  /** Artifact overview → Updated on row (16px). */
  updatedOn: KgCalendarIcon,
  /** Data story overview → Owner row (16px). */
  owner: KgUserIcon,
  /** Data story overview → Shared with row (16px). */
  sharedWith: KgUsersIcon,
  /** Data story overview → Artifacts section (16px). */
  artifacts: KgArtifactIcon,
  /** Report overview → Source row (16px). */
  source: KgDatabaseIcon,
  /** Overview → Columns row (16px). */
  columns: KgColumnsIcon,
  /** Relationship tab (16px). */
  relationship: KgRelationshipIcon,
  /** Metrics tab (16px). */
  metric: KgMetricIcon,
  /** Metric formula section (16px). */
  formula: KgSigmaSquareIcon,
  /** Ontology overview → Description (16px). */
  description: KgDescriptionIcon,
  /** Ontology → Links tab (16px). */
  links: KgLinksIcon,
  /** Ontology rules → Business rules (16px). */
  businessRules: KgBusinessRulesIcon,
  /** Ontology rules → Tribal knowledge (16px). */
  tribalKnowledge: KgTribalKnowledgeIcon,
} as const satisfies Record<
  string,
  ComponentType<KgInspectorIconProps>
>;

export type KgInspectorIconKey = keyof typeof kgInspectorIcons;

export const KG_INSPECTOR_ICON_DEFAULT_SIZE: Record<
  KgInspectorIconKey,
  number
> = {
  tablePanel: 14,
  ontologyPanel: 14,
  metricPanel: 14,
  reportPanel: 14,
  reportNativePanel: 14,
  reportRetrievedPanel: 14,
  dataStoryPanel: 14,
  artifactPanel: 14,
  dataModelPanel: 14,
  format: 16,
  updatedOn: 16,
  owner: 16,
  sharedWith: 16,
  artifacts: 16,
  source: 16,
  columns: 16,
  relationship: 16,
  metric: 16,
  formula: 16,
  description: 16,
  links: 16,
  businessRules: 16,
  tribalKnowledge: 16,
};
